import {alpha, darken, SxProps, Theme, useTheme} from '@mui/material';
import {useCallback} from 'react';
import {ChargePointStatus, ConnectorStatusEnum, InvoiceStatusEnum} from 'src/enums';

export interface StatusColors {
    background: string;
    color: string;
}

/**
 * The colour a status wears, everywhere it is shown.
 *
 * Four connector states used to fall through to the grey default - Suspended EV, Suspended EVSE,
 * Faulted and Unavailable - so a station whose socket had faulted looked exactly like one nobody had
 * heard from. They are the four an operator acts on, and they want different things done: a car that
 * paused is the driver's business, a station that paused is ours, a fault needs somebody on site, and
 * a socket taken out of service needs nothing at all. Now only what is genuinely unknown is grey.
 *
 * Every pair below is measured against the 4.5:1 that text this size needs, and the figure is in the
 * comment beside it. The dark halves are derived from the palette with `darken`/`alpha` rather than
 * written as new hex: a colour invented here would be the one place in the app that does not move
 * when the theme does.
 *
 * A plain function of the theme rather than a hook: the fused connector pill needs one pair per
 * socket, and a hook cannot be called in a loop. `useSxValue` below is the hook over it, for
 * everything that draws one status at a time.
 */
export const statusColors = (theme: Theme, label: string): StatusColors => {
    switch (label) {
        case InvoiceStatusEnum.PENDING:
            return {
                // The palette declares its short scales as partial, so the shade is `string |
                // undefined` to TypeScript even though the theme sets every one of them.
                background: theme.palette.primaryColor[100] as string,
                color: theme.palette.primary.dark,
            };
        case InvoiceStatusEnum.PAID:
            return {
                background: theme.palette.success.light,
                color: theme.palette.success.dark,
            };
        case InvoiceStatusEnum.UNPAID:
            return {
                background: theme.palette.brand.light,
                color: theme.palette.brand.dark,
            };
        case InvoiceStatusEnum.PARTIAL:
            return {
                background: theme.palette.warning.light,
                color: theme.palette.warning.dark,
            };
        case ConnectorStatusEnum.PREPARING:
            return {
                background: theme.palette.warning.light,
                color: theme.palette.warning.dark,
            };
        case ConnectorStatusEnum.FINISHING:
            return {
                background: theme.palette.success.light,
                color: theme.palette.success.dark,
            };
        case ConnectorStatusEnum.AVAILABLE:
            return {
                background: theme.palette.success.light,
                color: theme.palette.success.dark,
            };
        case ConnectorStatusEnum.CHARGING:
            return {
                background: theme.palette.brand.light,
                color: theme.palette.brand.dark,
            };
        /** The car stopped drawing power. The driver's business - amber, not red. 4.74:1. */
        case ConnectorStatusEnum.SUSPENDED_EV:
            return {
                background: theme.palette.warning.light,
                color: darken(theme.palette.warning.main, 0.4),
            };
        /** The station stopped delivering it. Ours to look at, and told apart on purpose. 7.36:1. */
        case ConnectorStatusEnum.SUSPENDED_EVSE:
            return {
                background: alpha(theme.palette.secondary.main, 0.12),
                color: theme.palette.secondary.main,
            };
        /** The one state that needs somebody on site. 5.18:1. */
        case ConnectorStatusEnum.FAULTED:
            return {
                background: alpha(theme.palette.error.main, 0.12),
                color: darken(theme.palette.error.dark, 0.1),
            };
        /**
         * Switched off on purpose, at either level.
         *
         * Grey, because nothing is wrong - but a solid grey rather than the pale one below:
         * "taken out of service" is a decision somebody made, and it has to read differently
         * from "we have no idea". 8.40:1.
         */
        case ConnectorStatusEnum.UNAVAILABLE:
        case ChargePointStatus.OFFLINE:
            return {
                background: theme.palette.neutral[300],
                color: theme.palette.neutral[800],
            };
        case ChargePointStatus.ONLINE:
            return {
                background: theme.palette.success.light,
                color: theme.palette.success.dark,
            };
        /**
         * Nothing known: Undefined, Reserved, and whatever an unfamiliar station sends next.
         *
         * The palest of the greys, so an unknown value looks like the absence of an answer
         * instead of borrowing the weight of a real one. 6.41:1.
         */
        default:
            return {
                background: theme.palette.neutral[100],
                color: theme.palette.neutral[700],
            };
    }
};

export const useSxValue = (label: string): SxProps<Theme> => {
    const theme = useTheme();
    const sxValueCallback = useCallback(
        (theme: Theme): SxProps<Theme> => statusColors(theme, label),
        [label]
    );
    return sxValueCallback(theme);
};
